import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DatePicker, { registerLocale } from 'react-datepicker';
import pt from 'date-fns/locale/pt';
import de from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';
import { useService } from 'contexts/ServiceContextProvider';
import {useTranslation} from "react-i18next";
import "../../css/components/Calendar/VacationRequestModal.css";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import InfoIcon from '@mui/icons-material/Info';
import { formattedHistory } from "./formatVacationHistory"
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';


const EditVacationModal = ({ open, handleClose, vacationData, workspaceId, handleSubmitEdit }) => {
  const { t } = useTranslation();
  registerLocale('pt', pt);
  registerLocale('de', de);

  const { getMyRemainingWorkspaceVacationDays } = useService();
  const [vacationDays, setVacationDays] = useState({
    remaining_vacation_days: 0,
    rollover_days: 0,
    used_vacation_days: 0,
    total_available_days: 0,
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'RV',
    date_start: new Date(),
    date_end: new Date(),
    visibility: false,
    do_not_deduct: false,
  });

  const [error, setError] = useState('');
  const [totalDays, setTotalDays] = useState(0);
  const [totalWeekdays, setTotalWeekdays] = useState(0);

  useEffect(() => {
    async function fetchVacationDays() {
      if (workspaceId) {
        try {
          const response = await getMyRemainingWorkspaceVacationDays(workspaceId);
          setVacationDays(response.data.vacation_balance);
        } catch (error) {
          console.error('Failed to fetch vacation days:', error);
        }
      }
    }
    fetchVacationDays();
  }, [workspaceId, getMyRemainingWorkspaceVacationDays]);

  useEffect(() => {
    if (vacationData) {
      setFormData({
        title: vacationData.title || '', 
        description: vacationData.description || '',
        type: vacationData.type || 'RV',
        date_start: vacationData.date_start ? parseISO(vacationData.date_start) : new Date(), 
        date_end: vacationData.date_end ? parseISO(vacationData.date_end) : new Date(), 
        visibility: vacationData.visibility !== undefined ? vacationData.visibility : false,
        do_not_deduct: vacationData.do_not_deduct || false,
      });
    }
  }, [vacationData]);
  

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (name, date) => {
    let updatedData = { ...formData, [name]: date };

    if (name === 'date_start' && date > formData.date_end) {
      setError(t("calendar.vacation_err1"));
    } else if (name === 'date_end' && date < formData.date_start) {
      setError(t("calendar.vacation_err2"));
    } else {
      setError('');

      const start = new Date(updatedData.date_start);
      const end = new Date(updatedData.date_end);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setTotalDays(diffDays);

      const weekdaysCount = getWeekdaysCount(start, end);
      setTotalWeekdays(weekdaysCount);
    }

    setFormData(updatedData);
  };

  const getWeekdaysCount = (start, end) => {
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const handleVisibilityChange = (event) => {
    setFormData({
      ...formData,
      visibility: event.target.checked,
    });
  };

  const handleDeductabilityChange = (event) => {
    setFormData({
      ...formData,
      do_not_deduct: event.target.checked,
    });
  };

  const handleSubmit = () => {
    if (formData.date_start > formData.date_end) {
      setError(t("calendar.vacation_err2"));
      return;
    }

    const updatedVacationData = {
      ...formData,
      date_start: format(formData.date_start, 'yyyy-MM-dd'),
      date_end: format(formData.date_end, 'yyyy-MM-dd'),
    };

    handleSubmitEdit(updatedVacationData);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="request-vacation-modal-title"
      aria-describedby="request-vacation-modal-description"
    >
      <Box className="request-vacation-main-style">
        <Typography id="request-vacation-modal-title" variant="h6" component="h2" className="white-txt-color">
          {t("calendar.edit_vacation_request")}
        </Typography>
        <TextField
          label={t("calendar.event_title")}
          name="title"
          value={formData.title}
          fullWidth
          margin="normal"
          variant="outlined"
          disabled
          className="request-vacation-disabled"
          InputLabelProps={{ className: "request-vacation-type-title" }}
          InputProps={{
            className: "request-vacation-type-select"
          }}
        />
        <TextField
          label={t("calendar.event_description")}
          name="description"
          value={formData.description}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          rows={3}
          InputLabelProps={{ className: "request-vacation-type-title" }}
          InputProps={{
            className: "request-vacation-type-select",
          }}
          onChange={handleChange}
        />
        <FormControl margin="normal" className="request-vacation-form">
          <InputLabel className="request-vacation-type-title">{t("calendar.vacation_type")}</InputLabel>
          <Select
            label={t("calendar.vacation_type")}
            name="type"
            value={formData.type}
            onChange={handleChange}
            variant="outlined"
            className="request-vacation-type-select"
            MenuProps={{
              PaperProps: {
                className: "request-vacation-type-select",
              },
            }}
          >
            <MenuItem value="RV">{t("calendar.vacation_type1")}</MenuItem>
            <MenuItem value="SL">{t("calendar.vacation_type2")}</MenuItem>
            <MenuItem value="PL">{t("calendar.vacation_type3")}</MenuItem>
            <MenuItem value="MPL">{t("calendar.vacation_type4")}</MenuItem>
          </Select>
        </FormControl>
        <div className="request-vacation-date-info">
          <Box className="request-vacation-date-picker">
            <DatePicker
              selected={formData.date_start}
              onChange={(date) => handleDateChange('date_start', date)}
              dateFormat="yyyy-MM-dd"
              selectsStart
              startDate={formData.date_start}
              endDate={formData.date_end}
              customInput={
                <TextField
                  label={t("global.start_date")}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ className: "request-vacation-type-title" }}
                  InputProps={{
                    className: "request-vacation-type-select",
                  }}
                />
              }
              locale={t("global.short_lng")}
            />
            <DatePicker
              selected={formData.date_end}
              onChange={(date) => handleDateChange('date_end', date)}
              dateFormat="yyyy-MM-dd"
              selectsEnd
              startDate={formData.date_start}
              endDate={formData.date_end}
              minDate={formData.date_start}
              customInput={
                <TextField
                  label={t("calendar.event_end_date")}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ className: "request-vacation-type-title" }}
                  InputProps={{
                    className: "request-vacation-type-select",
                  }}
                />
              }
              locale={t("global.short_lng")}
            />
          </Box>
          <Box className="request-vacation-info-box">
            <Typography variant="body1" className="request-vacation-info-title">
              <strong>{t("calendar.vacation_info")}</strong>
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_per_year", {count: vacationDays.remaining_vacation_days})}
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_rollovers", { count: vacationDays.rollover_days })}
              {vacationDays.rollover_days > 0 && (
                <ScrollAwareTooltip
                  title={<span dangerouslySetInnerHTML={{ __html: formattedHistory(vacationDays.rollover_days_history, t) }} />}
                >
                  <InfoIcon className="white-txt-color" />
                </ScrollAwareTooltip>
              )}
            </Typography>

              

            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_used_days", {count: vacationDays.used_vacation_days})}
            </Typography>
            <Typography variant="body2" className="request-vacation-type-title">
              {t("calendar.vacation_total_days", {count: vacationDays.total_available_days})}
            </Typography>
            {(vacationDays.pending_vacation_days || 0) > 0 && (
                <Typography variant="body2" className="request-vacation-type-title" style={{ color: '#f0a500' }}>
                  {t("calendar.vacation_pending_days", { count: vacationDays.pending_vacation_days })}
                </Typography>
            )}
          </Box>
        </div>

        <div className="request-vacation-total-days">
          <Typography variant="subtitle1" className="request-vacation-total-count">
            {t("calendar.vacation_total_count", { count1: totalDays })}
          </Typography>
          <Typography variant="subtitle1">
            {t("calendar.vacation_total_count_weekdays", { count1: totalWeekdays})}
          </Typography>
        </div>

        {error && (
          <Typography variant="body2" className="request-vacation-error">
            {error}
          </Typography>
        )}
        <Box className="request-vacation-flex">
          <FormControlLabel
            control={
              <Switch
                checked={formData.visibility}
                onChange={handleVisibilityChange}
                name="visibility"
                color="primary"
              />
            }
            label={t("calendar.event_public")}
            className="request-vacation-total-count"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.do_not_deduct}
                onChange={handleDeductabilityChange}
                name="do_not_deduct"
                color="primary"
              />
            }
            label={t("calendar.vacation_no_deduct")}
            className="request-vacation-total-count"
          />
        </Box>
        {(!formData.visibility || formData.do_not_deduct) && (
          <Box className="request-vacation-admins-info-box">
            {!formData.visibility && (
              <Typography variant="body2" className="request-vacation-type-title">
                {t("calendar.vacation_admins_info1")}
              </Typography>
            )}
            {formData.do_not_deduct && (
              <Typography variant="body2" className="request-vacation-total-count">
                {t("calendar.vacation_admins_info2")}
              </Typography>
            )}
          </Box>
        )}
        <Box className="buttons-flex request-vacation-buttons-box">
          <Button
              variant="outlined"
              className="decline-button request-vacation-cancel-button"
              onClick={handleClose}
          >
            <CancelIcon style={{ marginRight: 4 }} />
            {t("global.cancel")}
          </Button>
          <Button
            variant="contained"
            className="accept-button"
            onClick={handleSubmit}
            disabled={!!error}
          >
            <CheckCircleIcon style={{ marginRight: 4 }} />
            {t("calendar.vacation_submit")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditVacationModal;
